// invest_FE/src/components/modals/BecomeSellerModal.js
import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import './BecomeSellerModal.css';
import { UserContext } from '../../context/UserContext';
import { ConfigContext } from '../../context/ConfigContext';
import { convertCreditsToTargetCurrency, formatCurrency } from '../../utils/priceCalculator';

const BecomeSellerModal = ({ isOpen, onClose, onConfirm }) => {
  const { user } = useContext(UserContext);
  const { config, isLoading: isConfigLoading } = useContext(ConfigContext);
  const { t } = useTranslation();
  const lang = useLanguage();
  const [agreed, setAgreed] = useState(false);

  const price = useMemo(() => {
    if (isConfigLoading || !config) {
      return null;
    }

    try {
      const userCurrency = user?.preferences?.currency || 'ILS';
      const priceInCredits = config.creditsForInquiry;
      const rates = {
        usdToIlsRate: config.usdToIlsRate,
        eurToIlsRate: config.eurToIlsRate,
        creditPointValueILS: config.creditPointValueILS,
      };

      const convertedPrice = convertCreditsToTargetCurrency(priceInCredits, userCurrency, rates);
      return formatCurrency(convertedPrice, userCurrency);
    } catch (error) {
      console.error('Failed to calculate price for seller modal:', error);
      return 'N/A';
    }
  }, [config, isConfigLoading, user]);

  const handleCheckboxChange = (e) => {
    setAgreed(e.target.checked);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{t('becomeSellerModal.title')}</h2>
        <p>{t('becomeSellerModal.intro')}</p>
        <p>
          <Trans i18nKey="becomeSellerModal.limitedTimeOffer">
            For a limited time, it's <strong>free to become a seller</strong> and also <strong>free to list your properties</strong>.
          </Trans>
        </p>
        <h4>{t('becomeSellerModal.howItWorksTitle')}</h4>
        <ul>
          <li dangerouslySetInnerHTML={{ __html: t('becomeSellerModal.howItWorksPoint1') }} />
          <li dangerouslySetInnerHTML={{ __html: t('becomeSellerModal.howItWorksPoint2') }} />
          <li dangerouslySetInnerHTML={{ __html: t('becomeSellerModal.howItWorksPoint3', { price: isConfigLoading ? t('becomeSellerModal.loadingPrice') : price }) }} />
        </ul>
        <h4>{t('becomeSellerModal.keyBenefitsTitle')}</h4>
        <ul>
          <li dangerouslySetInnerHTML={{ __html: t('becomeSellerModal.keyBenefitsPoint1') }} />
          <li dangerouslySetInnerHTML={{ __html: t('becomeSellerModal.keyBenefitsPoint2') }} />
          <li dangerouslySetInnerHTML={{ __html: t('becomeSellerModal.keyBenefitsPoint3') }} />
        </ul>
        <p>{t('becomeSellerModal.outro')}</p>
        <p className="disclaimer" dangerouslySetInnerHTML={{ __html: t('becomeSellerModal.disclaimer') }} />
        <div className="terms-agreement">
          <input type="checkbox" id="terms-agree" checked={agreed} onChange={handleCheckboxChange} />
          <label htmlFor="terms-agree">
            <Trans
              i18nKey="becomeSellerModal.agreeToTerms"
              components={{
                1: <Link to={`/${lang}/provider-terms-of-use`} target="_blank" rel="noopener noreferrer" />,
              }}
            ></Trans>
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose} className="cancel-btn">
            {t('becomeSellerModal.buttonCancel')}
          </button>
          {agreed === true ? (
            <button type="button" onClick={onConfirm} className="confirm-btn">
              {t('becomeSellerModal.buttonConfirm')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BecomeSellerModal;
